import Logger from '@/utils/logger'
import { 
  getHouseList as apiGetHouseList,
  getHouseDetail as apiGetHouseDetail,
  updateHouse as apiUpdateHouse,
  updateHouseDetails as apiUpdateHouseDetails,
  deleteHouse as apiDeleteHouse,
  batchOperateHouses as apiBatchOperateHouses
} from '@/api/houses'

const state = {
  houseList: [],
  currentHouse: null,
  loading: false,
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  }
}

const mutations = {
  SET_HOUSE_LIST(state, { list, pagination }) {
    state.houseList = list
    state.pagination = { ...state.pagination, ...pagination }
  },
  SET_CURRENT_HOUSE(state, house) {
    state.currentHouse = house
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  UPDATE_HOUSE_IN_LIST(state, updatedHouse) {
    const index = state.houseList.findIndex(house => house.id === updatedHouse.id)
    if (index !== -1) {
      state.houseList.splice(index, 1, { ...state.houseList[index], ...updatedHouse })
    }
  },
  REMOVE_HOUSE_FROM_LIST(state, houseId) {
    const index = state.houseList.findIndex(house => house.id === houseId)
    if (index !== -1) {
      state.houseList.splice(index, 1)
    }
  }
}

const actions = {
  async fetchHouses({ commit }, params) {
    commit('SET_LOADING', true)
    try {
      const response = await apiGetHouseList(params)
      
      // 处理返回的数据，适配新的接口结构
      const processedList = response.data.list.map((house) => ({
        id: house.id,
        title: house.title,
        cover_image: house.cover_image,
        area: house.area,
        layout: house.layout,
        rent: house.rent,
        orientation: house.orientation,
        can_register_company: house.can_register_company,
        cooling_fee: house.cooling_fee,
        remarks: house.remarks,
        transaction_type: house.transaction_type,
        favorite_count: house.favorite_count || 0,
        publisher: house.user_info ? {
          id: house.user_info.id,
          nickname: house.user_info.nickname,
          phone: house.user_info.phone
        } : null,
        status: house.status,
        type: house.type,
        province: house.province,
        city: house.city,
        district: house.district,
        address: house.address,
        contact_phone: house.contact_phone,
        distance_info: house.distance_info,
        tags: Array.isArray(house.tags) ? house.tags : (house.tags ? JSON.parse(house.tags) : []),
        is_featured: house.is_featured,
        created_at: house.created_at,
        updated_at: house.updated_at,
        elevator: house.elevator,
        network: house.network
      }))
      
      commit('SET_HOUSE_LIST', {
        list: processedList,
        pagination: {
          page: response.data.page,
          limit: response.data.limit,
          total: response.data.total
        }
      })
      
      return { success: true, data: response.data }
    } catch (error) {
      console.error('获取房源列表失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchHouse({ commit }, id) {
    try {
      const response = await apiGetHouseDetail(id)
      
      // 处理房源详情数据
      const houseDetail = {
        ...response.data,
        tags: Array.isArray(response.data.tags) ? response.data.tags : (response.data.tags ? JSON.parse(response.data.tags) : []),
        publisher: response.data.user_info ? {
          id: response.data.user_info.id,
          nickname: response.data.user_info.nickname,
          phone: response.data.user_info.phone,
          avatar_url: response.data.user_info.avatar_url
        } : null
      }
      
      commit('SET_CURRENT_HOUSE', houseDetail)
      return { success: true, data: houseDetail }
    } catch (error) {
      console.error('获取房源详情失败:', error)
      throw error
    }
  },
  
  async updateHouse({ commit }, { id, data }) {
    try {
      const response = await apiUpdateHouse(id, data)
      
      commit('UPDATE_HOUSE_IN_LIST', response.data)
      commit('SET_CURRENT_HOUSE', response.data)
      
      // 记录操作日志
      Logger.recordHouseAction('更新', response.data, data, { success: true })
      
      return response
    } catch (error) {
      console.error('更新房源失败:', error)
      throw error
    }
  },
  
  async updateHouseDetails({ commit }, { id, data }) {
    try {
      const response = await apiUpdateHouseDetails(id, data)
      
      commit('UPDATE_HOUSE_IN_LIST', response.data)
      commit('SET_CURRENT_HOUSE', response.data)
      
      // 记录操作日志
      Logger.recordHouseAction('更新详情', response.data, data, { success: true })
      
      return response
    } catch (error) {
      console.error('更新房源详情失败:', error)
      throw error
    }
  },
  
  async deleteHouse({ commit, state }, id) {
    try {
      const house = state.houseList.find(house => house.id === id)
      const response = await apiDeleteHouse(id)
      
      commit('REMOVE_HOUSE_FROM_LIST', id)
      
      // 记录操作日志
      if (house) {
        Logger.recordHouseAction('删除', house, { id }, response.data)
      }
      
      return response
    } catch (error) {
      console.error('删除房源失败:', error)
      throw error
    }
  },
  
  async batchOperateHouses({ commit }, { action, house_ids, data }) {
    try {
      const response = await apiBatchOperateHouses({ action, house_ids, data })
      
      // 根据操作类型更新本地状态
      if (action === 'delete') {
        house_ids.forEach(id => {
          commit('REMOVE_HOUSE_FROM_LIST', id)
        })
      } else if (action === 'update_status' || action === 'set_featured') {
        // 重新获取列表以确保数据一致性
        // 这里可以选择重新调用 fetchHouses 或者更新本地状态
      }
      
      // 记录操作日志
      Logger.recordHouseAction(`批量${action}`, { ids: house_ids }, { action, house_ids, data }, response.data)
      
      return response
    } catch (error) {
      console.error('批量操作房源失败:', error)
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}